import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { ZoomTranscriptView } from '../../../extViewmodels/sdk/models/Zoom';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { getConversationChatMessageQueryKey } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { IDemoRecording } from '../../queries/DemoRecordings/useDemoRecordings';

const SALES_DEMO_CALL_COACHING_AGENT = 'SalesDemoCallCoachingAgent';

export const useSingleDemoAnalysis = () => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useSingleDemoAnalysis');

	const [isAnalysisModalOpen, setIsAnalysisModalOpen] = React.useState(false);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState<boolean>(false);
	const [transcriptionText, setTranscriptionText] = React.useState<string>('');
	const [isNewAnalysisSession, setIsNewAnalysisSession] = React.useState<boolean>(true);
	const [selectedRecording, setSelectedRecording] = React.useState<IDemoRecording | null>(null);
	const hasStartedConversationRef = React.useRef<boolean>(false);

	// Query for transcript - only enabled when a recording is selected
	const transcriptsQuery = useQuery({
		queryKey: ['lead/demos/transcripts', selectedRecording?.uuid],
		queryFn: async (): Promise<ZoomTranscriptView[]> => {
			return new Promise<ZoomTranscriptView[]>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceWithBulkOperationResult<ZoomTranscriptView>(
					'lead/demos/transcripts',
					'POST',
					{ meetingIds: [selectedRecording!.uuid] },
					data => {
						resolve(data.succeeded);
					},
					reject
				);
			});
		},
		enabled: !!selectedRecording,
		staleTime: 1000 * 60 * 60 * 1, // 1 hour
		meta: {
			toastErrorMessage: 'Failed to load transcript for analysis.',
			errorLogging: {
				category: 'useSingleDemoAnalysis',
				action: 'useDemoTranscriptsQuery',
			},
		},
	});

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: (conversation: any) => {
			const chatMessages: {
				role: Api.AIChatRole;
				text: string;
				id: string | null;
			}[] = [
				{
					role: Api.AIChatRole.User,
					text: `Demo Recording:\n${transcriptionText}`,
					id: null,
				},
			];

			aiPromptMutation.mutate({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				conversationId: conversation.id,
				chatRequest: {
					chatMessages,
				},
			});
		},
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			conversationId,
			chatRequest,
		}: {
			assistantName: string;
			conversationId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${conversationId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event: any, params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;

			if (messageText) {
				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: params.conversationId,
					}),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							let updatedText = lastAssistantMessage.text;

							updatedText = data.isDelta === false ? messageText : updatedText + messageText;

							return [
								...messages,
								{
									...lastAssistantMessage,
									text: updatedText,
								},
							];
						} else {
							return [
								...(messages || []),
								{
									id: data.message.id,
									role: Api.AIChatRole.Assistant,
									text: messageText,
									creationDate: new Date().toISOString(),
								},
							];
						}
					}
				);
			}
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPromptMutation', error);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
		},
	});

	// When transcript is loaded, format and create conversation
	React.useEffect(() => {
		if (
			transcriptsQuery.isSuccess &&
			transcriptsQuery.data &&
			selectedRecording &&
			!hasStartedConversationRef.current
		) {
			if (transcriptsQuery.data.length === 0) {
				errorMessages.push({
					messages: ['No transcript found for this recording.'],
					title: 'No Transcript',
				});
				setSelectedRecording(null);
				return;
			}

			const transcript = transcriptsQuery.data[0];
			const formattedTranscript = `Meeting: ${transcript.topic || selectedRecording.topic}\nDate: ${transcript.startTime || selectedRecording.startTime}\nDuration: ${transcript.durationMinutes || selectedRecording.duration} minutes\n\nTranscript:\n${transcript.transcriptText}`;

			setTranscriptionText(formattedTranscript);

			// Open modal now that transcript is loaded
			setIsAnalysisModalOpen(true);

			// Mark that we've started the conversation
			hasStartedConversationRef.current = true;

			// Create conversation and start analysis
			aiAssistantCreateConversationMutation.mutate({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				source: 'DemoRecordingsModal',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transcriptsQuery.isSuccess, transcriptsQuery.data, selectedRecording]);

	// Handle transcript loading errors
	React.useEffect(() => {
		if (transcriptsQuery.isError) {
			setIsAnalysisModalOpen(false);
			setSelectedRecording(null);
		}
	}, [transcriptsQuery.isError]);

	// Start analysis for a single recording
	const startAnalysis = (recording: IDemoRecording) => {
		aiPromptMutation.reset();
		setHasFetchedFeedback(false);
		setTranscriptionText('');
		setIsNewAnalysisSession(true);
		setSelectedRecording(recording);
		hasStartedConversationRef.current = false;
	};

	const closeAnalysis = () => {
		aiPromptMutation.reset();
		setIsAnalysisModalOpen(false);
		setHasFetchedFeedback(false);
		setTranscriptionText('');
		setIsNewAnalysisSession(true);
		setSelectedRecording(null);
		hasStartedConversationRef.current = false;
	};

	// Reset tracking when starting a new AI session
	React.useEffect(() => {
		if (!hasFetchedFeedback && isNewAnalysisSession) {
			setIsNewAnalysisSession(false);
		}
	}, [hasFetchedFeedback, isNewAnalysisSession]);

	const sendFollowUp = (question: string) => {
		if (!question.trim()) return;

		reactQueryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: aiAssistantCreateConversationMutation.data.id,
			}),
			(messages: Api.AIChatMessage[]) => {
				return [...messages, { role: Api.AIChatRole.User, text: question }];
			}
		);

		// Reset AI response for new streaming response but keep conversation history
		setHasFetchedFeedback(false);

		aiPromptMutation.mutate({
			assistantName: SALES_DEMO_CALL_COACHING_AGENT,
			conversationId: aiAssistantCreateConversationMutation.data.id,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: question,
						id: null,
					},
				],
			},
		});
	};

	return {
		isAnalysisModalOpen,
		startAnalysis,
		closeAnalysis,
		isLoading:
			transcriptsQuery.isFetching || aiAssistantCreateConversationMutation.isPending || aiPromptMutation.isLoading,
		isLoadingTranscript: transcriptsQuery.isFetching,
		isCreatingConversation: aiAssistantCreateConversationMutation.isPending,
		sendFollowUp,
		conversationId: aiAssistantCreateConversationMutation.data?.id,
		aiPromptMutation,
	};
};
